import { DEFAULT_RETRY_OPTIONS } from '../../const';
import type { ResponsePayload } from '../../types';
import type { PrepareAndMakeRequestOptions } from '../types';
import { composeQuery } from './composeQuery';
import { composeRequestBody } from './composeRequestBody';
import { getAccessToken } from './getAccessToken';
import { mergeRetryOptions } from './mergeRetryOptions';
import { request } from './request';

// Adds support to legacy services that exist outside of frontend/api
const _prepareAndMakeRequest = <T extends ResponsePayload>(
  url: string,
  extendedOptions: PrepareAndMakeRequestOptions = {},
): Promise<T> => {
  const apiUrl = process?.env?.API_URL ?? '';
  const protocolPrefix =
    typeof location !== 'undefined' ? location.protocol + '//' : 'http://';
  const isProtocolSpecified = /http.?:\/\//.test(apiUrl);
  const isInsideContainer = location.hostname === 'nginx';
  const API_URL = isInsideContainer
    ? protocolPrefix + 'nginx/api'
    : isProtocolSpecified
    ? apiUrl
    : protocolPrefix + apiUrl;

  const fetchUrl = url.includes('://') ? url : API_URL + url;

  //For development from localhost against production APIs, uncomment (and make fetchUrl `let`):
  //* This is necessary for `TARGET_ENV="localAgainstProduction" yarn dev` because localhost is http and we require requests to api.paperlessparts.com to be https
  //* But in other cases we want the protocol to match for the static js/css/html and the API requests
  //if (fetchUrl.includes('http:')) {
  //  fetchUrl = fetchUrl.replace('http', 'https');
  //}

  const {
    headers: _headers,
    body: _body,
    query: _query,
    retryOptions: _retryOptions,
    ...restOptions
  } = extendedOptions;
  const query = composeQuery(_query);
  const { body, headerOverrides } = composeRequestBody(_body);
  const token = getAccessToken();

  const headers: RequestInit['headers'] = {
    // Start with headers from options.
    ..._headers,
    // Apply header overrides based on body, if any. Main purpose is to
    // override content type to 'application/json' if we converted the body to
    // JSON.
    ...headerOverrides,
    // Add authorization token.
    ...(token && { Authorization: `Token ${token}` }),
  };

  const retryOptions = mergeRetryOptions({
    baseRetryOptions: DEFAULT_RETRY_OPTIONS,
    retryOptionsToMerge: _retryOptions,
  });

  const requestOptions: Parameters<typeof request>[1] = {
    method: extendedOptions.method ?? 'GET',
    ...(body && { body }),
    headers,
    ...(fetchUrl.includes(API_URL) && { credentials: 'include' }),
    retryOptions,
    ...restOptions,
  };

  return request<T>(`${fetchUrl}${query}`, requestOptions, {
    shouldConvertResponseKeysToCamelCase:
      extendedOptions.shouldConvertResponseKeysToCamelCase ?? true,
  });
};

const REQUEST_METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

type RequestMethod = (typeof REQUEST_METHODS)[number];

type RequestImplementationFunction = <T extends ResponsePayload>(
  url: string,
  options?: Omit<PrepareAndMakeRequestOptions, 'method'>,
) => Promise<T>;

const REQUEST_METHOD_IMPLEMENTATION_ENTRIES = REQUEST_METHODS.map((method) => {
  const methodUppercase = method.toUpperCase();

  const entry: [RequestMethod, RequestImplementationFunction] = [
    method,
    (
      url: string,
      options?: Omit<Parameters<PrepareAndMakeRequest>[1], 'method'>,
      //IMPORTANT: the `method` in options must be upper case
      //* If not, there is a CORS issue for `patch` method because `Access-Control-Allow-Methods`
      //  on server lists PATCH as uppercase.
    ) => _prepareAndMakeRequest(url, { ...options, method: methodUppercase }),
  ];

  return entry;
});

type RequestImplementations = Record<
  RequestMethod,
  RequestImplementationFunction
>;

type PrepareAndMakeRequest = RequestImplementationFunction &
  RequestImplementations;

const REQUEST_METHOD_IMPLEMENTATIONS = Object.fromEntries(
  REQUEST_METHOD_IMPLEMENTATION_ENTRIES,
) as RequestImplementations;

// Add get, post, put, patch and delete methods
export const prepareAndMakeRequest: PrepareAndMakeRequest = Object.assign(
  _prepareAndMakeRequest,
  REQUEST_METHOD_IMPLEMENTATIONS,
);
